import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styled, { keyframes } from 'styled-components';

import MenuCard from './MenuCard';

import apiClient from '../api/client';
import { menuCategories, menuProducts } from '../data/menuData';

const ALL_CATEGORY = 'TUTTI';

const DEFAULT_ACTIVE_BACKGROUND_COLOR = '#df2025';
const DEFAULT_ACTIVE_TEXT_COLOR = '#ffffff';
const DEFAULT_FILTER_FONT_SIZE = 14;

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const FILTER_FONT_FAMILIES = {
  default: 'inherit',
  arial: 'Arial, sans-serif',
  georgia: 'Georgia, serif',
  times_new_roman: '"Times New Roman", serif',
  trebuchet: '"Trebuchet MS", sans-serif',
  impact: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif',
  courier_new: '"Courier New", monospace',
};

function normalizeCategoryValue(value) {
  return String(value || '')
    .trim()
    .toLocaleUpperCase('it-IT');
}

function normalizeHexColor(value, fallback) {
  return HEX_COLOR_PATTERN.test(String(value || '')) ? value : fallback;
}

function normalizeFilterFontSize(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return DEFAULT_FILTER_FONT_SIZE;
  }

  return Math.min(24, Math.max(10, Math.round(numericValue)));
}

function getFilterFontFamily(value) {
  return FILTER_FONT_FAMILIES[value] || FILTER_FONT_FAMILIES.default;
}

function getResponseList(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.value)) {
    return data.value;
  }

  return [];
}

function formatEuroPrice(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return `€${numericValue.toFixed(2).replace('.', ',')}`;
}

function normalizeApiProduct(product) {
  if (
    !product ||
    product.is_active === false ||
    typeof product.name !== 'string' ||
    !product.name.trim()
  ) {
    return null;
  }

  const categoryValue = normalizeCategoryValue(product.category?.name);

  if (!categoryValue) {
    return null;
  }

  const discountPercent = Number(product.discount_percent) || 0;
  const hasDiscount =
    discountPercent > 0 &&
    product.discounted_price !== null &&
    product.discounted_price !== undefined;

  const displayedPrice = hasDiscount ? product.discounted_price : product.price;

  const fallbackKey = `${categoryValue}-${product.name}`;

  return {
    id: `api-${product.id ?? product.slug ?? fallbackKey}`,
    name: product.name.trim(),
    price: formatEuroPrice(displayedPrice),
    originalPrice: formatEuroPrice(product.price),
    discountedPrice: formatEuroPrice(product.discounted_price),
    discountPercent,
    description: String(product.description || '').trim(),
    category: categoryValue,
    image: String(product.image || ''),
    ingredients: Array.isArray(product.ingredients) ? product.ingredients : [],
    showIngredients: product.show_ingredients !== false,
    badge: product.final_badge || product.custom_badge || product.badge || '',
    isAvailable: product.is_available !== false,
  };
}

function createCategoryOption({
  key,
  label,
  value,
  activeBackgroundColor = DEFAULT_ACTIVE_BACKGROUND_COLOR,
  activeTextColor = DEFAULT_ACTIVE_TEXT_COLOR,
  font = 'default',
  fontSize = DEFAULT_FILTER_FONT_SIZE,
}) {
  const normalizedLabel = String(label || '').trim();
  const normalizedValue = normalizeCategoryValue(value || normalizedLabel);

  return {
    key,
    label: normalizedLabel.toLocaleUpperCase('it-IT'),
    value: normalizedValue,
    activeBackgroundColor: normalizeHexColor(
      activeBackgroundColor,
      DEFAULT_ACTIVE_BACKGROUND_COLOR,
    ),
    activeTextColor: normalizeHexColor(activeTextColor, DEFAULT_ACTIVE_TEXT_COLOR),
    fontFamily: getFilterFontFamily(font),
    fontSize: normalizeFilterFontSize(fontSize),
  };
}

const FALLBACK_CATEGORY_OPTIONS = menuCategories
  .filter((category) => normalizeCategoryValue(category) !== ALL_CATEGORY)
  .map((category) =>
    createCategoryOption({
      key: `fallback-${category}`,
      label: category,
      value: category,
    }),
  );

const swipeHintNudge = keyframes`
  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(var(--swipe-hint-distance));
  }
`;

const MenuWrapper = styled.section`
  width: 100%;

  padding: 25px 0 40px;

  background: ${({ $sectionBackgroundColor }) => $sectionBackgroundColor};
`;

const MenuContainer = styled.div`
  width: min(100% - 80px, 1400px);

  margin: 0 auto;

  @media (max-width: 900px) {
    width: min(100% - 40px, 1400px);
  }

  @media (max-width: 600px) {
    width: min(100% - 30px, 1400px);
  }
`;

/* =========================================================
   DESKTOP FILTER
   ========================================================= */

const DesktopCategories = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  gap: 22px;

  margin-bottom: 25px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const DesktopCategoryButton = styled.button`
  min-width: 110px;
  min-height: 45px;

  padding: 0 25px;

  border: 0;
  border-radius: 9px;

  background: ${({ $active, $activeBackgroundColor }) =>
    $active ? $activeBackgroundColor : '#ffffff'};

  color: ${({ $active, $activeTextColor }) => ($active ? $activeTextColor : '#151515')};

  font-family: ${({ $fontFamily }) => $fontFamily};
  font-size: ${({ $fontSize }) => `${$fontSize}px`};
  font-weight: 800;

  cursor: pointer;

  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.1);

  transition:
    transform 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid #ffd000;
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* =========================================================
   MOBILE MAGNETIC CATEGORY RAIL
   ========================================================= */

const MobileRailShell = styled.div`
  position: relative;

  display: none;

  width: 100%;

  margin-bottom: 25px;

  overflow: hidden;

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;

  background:
    radial-gradient(circle at 15% 0%, rgba(223, 32, 37, 0.18), transparent 38%),
    linear-gradient(145deg, #1b1b1b 0%, #090909 65%, #111111 100%);

  box-shadow:
    0 13px 28px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);

  @media (max-width: 900px) {
    display: block;
  }

  &::before,
  &::after {
    position: absolute;
    z-index: 2;
    top: 1px;
    bottom: 1px;

    width: 24px;

    pointer-events: none;

    content: '';
  }

  &::before {
    left: 0;

    background: linear-gradient(90deg, rgba(13, 13, 13, 0.96), rgba(13, 13, 13, 0));
  }

  &::after {
    right: 0;

    background: linear-gradient(270deg, rgba(13, 13, 13, 0.96), rgba(13, 13, 13, 0));
  }
`;

const MobileCategoriesRail = styled.div`
  display: flex;
  align-items: center;

  gap: 10px;

  width: 100%;

  padding: 9px 18px;

  overflow-x: auto;
  overflow-y: hidden;

  scroll-padding-inline: 18px;
  scroll-snap-type: x proximity;

  overscroll-behavior-inline: contain;

  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobileCategoryButton = styled.button`
  position: relative;

  flex: 0 0 auto;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 92px;
  min-height: 46px;
  max-width: min(72vw, 260px);

  padding: 0 21px;

  overflow: hidden;

  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.26)' : 'rgba(255, 255, 255, 0.09)')};

  border-radius: 999px;

  background: ${({ $active, $activeBackgroundColor }) =>
    $active
      ? $activeBackgroundColor
      : `
        linear-gradient(
          145deg,
          rgba(255, 255, 255, 0.1),
          rgba(255, 255, 255, 0.035)
        )
      `};

  color: ${({ $active, $activeTextColor }) => ($active ? $activeTextColor : '#eeeeee')};

  font-family: ${({ $fontFamily }) => $fontFamily};
  font-size: ${({ $fontSize }) => `${$fontSize}px`};
  font-weight: 900;
  letter-spacing: 0.35px;
  line-height: 1;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;

  cursor: pointer;

  scroll-snap-align: center;

  box-shadow: ${({ $active, $activeBackgroundColor }) =>
    $active
      ? `
        0 8px 18px ${$activeBackgroundColor}66,
        inset 0 1px 0 rgba(255, 255, 255, 0.25),
        inset 0 -2px 4px rgba(0, 0, 0, 0.22)
      `
      : `
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        0 4px 10px rgba(0, 0, 0, 0.16)
      `};

  transform: ${({ $active }) =>
    $active ? 'translateY(-1px) scale(1.02)' : 'translateY(0) scale(1)'};

  transition:
    transform 0.22s ease,
    background 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    color 0.22s ease;

  &::before {
    position: absolute;
    top: 2px;
    left: 18%;

    width: 64%;
    height: 1px;

    border-radius: 50%;

    background: ${({ $active }) =>
      $active ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.09)'};

    content: '';
  }

  &:focus-visible {
    z-index: 3;

    outline: 3px solid #ffd000;
    outline-offset: 2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px) scale(1.02);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SwipeHint = styled.span`
  position: absolute;
  z-index: 5;
  top: 50%;
  right: ${({ $side }) => ($side === 'right' ? '5px' : 'auto')};
  left: ${({ $side }) => ($side === 'left' ? '5px' : 'auto')};

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 34px;
  height: 34px;

  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;

  background:
    radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.14), transparent 42%),
    rgba(6, 6, 6, 0.88);

  color: #ffffff;

  pointer-events: none;
  user-select: none;

  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.35),
    0 0 16px rgba(223, 32, 37, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  transform: translateY(-50%);

  @media (min-width: 901px) {
    display: none;
  }
`;

const SwipeHintChevron = styled.span`
  --swipe-hint-distance: ${({ $side }) => ($side === 'left' ? '-8px' : '8px')};

  display: inline-block;

  font-size: 22px;
  font-weight: 900;
  line-height: 1;

  animation: ${swipeHintNudge} 0.72s ease 3;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* =========================================================
   PRODUCTS
   ========================================================= */

const ProductGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(4, minmax(0, 1fr));

  gap: 25px;

  @media (max-width: 1150px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  width: 100%;

  padding: 38px 20px;

  border: 1px dashed rgba(21, 21, 21, 0.2);
  border-radius: 14px;

  background: rgba(255, 255, 255, 0.55);

  color: #555555;

  font-size: 15px;
  font-weight: 700;
  text-align: center;
`;

function MenuSection({ menuSettings }) {
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
  const [apiCategories, setApiCategories] = useState([]);
  const [categoriesApiFailed, setCategoriesApiFailed] = useState(false);
  const [apiProducts, setApiProducts] = useState(null);
  const [swipeHintSide, setSwipeHintSide] = useState(null);

  const mobileRailRef = useRef(null);
  const mobileCategoryButtonsRef = useRef([]);

  const showCategoryFilter = menuSettings?.show_category_filter ?? true;

  const categoryOptions = useMemo(() => {
    const tuttiOption = createCategoryOption({
      key: 'tutti',
      label: ALL_CATEGORY,
      value: ALL_CATEGORY,
      activeBackgroundColor:
        menuSettings?.tutti_filter_active_background_color ?? DEFAULT_ACTIVE_BACKGROUND_COLOR,
      activeTextColor: menuSettings?.tutti_filter_active_text_color ?? DEFAULT_ACTIVE_TEXT_COLOR,
      font: menuSettings?.tutti_filter_font ?? 'default',
      fontSize: menuSettings?.tutti_filter_font_size ?? DEFAULT_FILTER_FONT_SIZE,
    });

    const apiOptions = apiCategories
      .filter(
        (category) =>
          category &&
          category.is_active !== false &&
          typeof category.name === 'string' &&
          category.name.trim(),
      )
      .map((category) =>
        createCategoryOption({
          key: `api-${category.id ?? category.slug ?? category.name}`,
          label: category.name,
          value: category.name,
          activeBackgroundColor: category.filter_active_background_color,
          activeTextColor: category.filter_active_text_color,
          font: category.filter_font,
          fontSize: category.filter_font_size,
        }),
      )
      .filter((category) => category.value !== ALL_CATEGORY);

    const visibleCategoryOptions = categoriesApiFailed ? FALLBACK_CATEGORY_OPTIONS : apiOptions;

    return [tuttiOption, ...visibleCategoryOptions];
  }, [
    apiCategories,
    categoriesApiFailed,
    menuSettings?.tutti_filter_active_background_color,
    menuSettings?.tutti_filter_active_text_color,
    menuSettings?.tutti_filter_font,
    menuSettings?.tutti_filter_font_size,
  ]);

  useEffect(() => {
    let isMounted = true;

    apiClient
      .get('/categories/')
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setCategoriesApiFailed(false);
        setApiCategories(getResponseList(response.data));
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        console.warn(
          error.message ||
            '[MenuSection] Impossibile caricare le categorie. Uso dei dati fallback.',
        );

        setCategoriesApiFailed(true);
        setApiCategories([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    apiClient
      .get('/items/')
      .then((response) => {
        if (!isMounted) {
          return;
        }

        const normalizedProducts = getResponseList(response.data)
          .map(normalizeApiProduct)
          .filter(Boolean);

        setApiProducts(normalizedProducts);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        console.warn(
          error.message || '[MenuSection] Impossibile caricare i prodotti. Uso dei dati fallback.',
        );

        setApiProducts(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const activeCategoryExists = categoryOptions.some(
      (category) => category.value === activeCategory,
    );

    if (!activeCategoryExists) {
      setActiveCategory(ALL_CATEGORY);
    }
  }, [activeCategory, categoryOptions]);

  useEffect(() => {
    if (!showCategoryFilter && activeCategory !== ALL_CATEGORY) {
      setActiveCategory(ALL_CATEGORY);
    }
  }, [showCategoryFilter, activeCategory]);

  const updateSwipeHint = useCallback(() => {
    if (
      !showCategoryFilter ||
      typeof window === 'undefined' ||
      !window.matchMedia('(max-width: 900px)').matches
    ) {
      setSwipeHintSide(null);
      return;
    }

    const rail = mobileRailRef.current;

    if (!rail) {
      setSwipeHintSide(null);
      return;
    }

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    const hasHorizontalOverflow = maxScrollLeft > 2;

    if (!hasHorizontalOverflow) {
      setSwipeHintSide(null);
      return;
    }

    const isAtStart = rail.scrollLeft <= 8;

    setSwipeHintSide(isAtStart ? 'right' : 'left');
  }, [showCategoryFilter]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mobileMediaQuery = window.matchMedia('(max-width: 900px)');
    const frameId = window.requestAnimationFrame(updateSwipeHint);

    mobileMediaQuery.addEventListener?.('change', updateSwipeHint);
    window.addEventListener('resize', updateSwipeHint);

    return () => {
      window.cancelAnimationFrame(frameId);

      mobileMediaQuery.removeEventListener?.('change', updateSwipeHint);
      window.removeEventListener('resize', updateSwipeHint);
    };
  }, [updateSwipeHint]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(updateSwipeHint);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [categoryOptions, updateSwipeHint]);

  useEffect(() => {
    if (
      !showCategoryFilter ||
      typeof window === 'undefined' ||
      !window.matchMedia('(max-width: 900px)').matches
    ) {
      return;
    }

    const activeIndex = categoryOptions.findIndex((category) => category.value === activeCategory);

    const rail = mobileRailRef.current;
    const activeButton = mobileCategoryButtonsRef.current[activeIndex];

    if (activeIndex < 0 || !rail || !activeButton) {
      return;
    }

    const targetScrollLeft =
      activeButton.offsetLeft - (rail.clientWidth - activeButton.offsetWidth) / 2;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    rail.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [activeCategory, categoryOptions, showCategoryFilter]);

  const productSource = apiProducts === null ? menuProducts : apiProducts;

  const products = useMemo(() => {
    if (!showCategoryFilter || activeCategory === ALL_CATEGORY) {
      return productSource;
    }

    return productSource.filter(
      (product) => normalizeCategoryValue(product.category) === activeCategory,
    );
  }, [activeCategory, productSource, showCategoryFilter]);

  const handleCategorySelect = (categoryValue) => {
    setActiveCategory((currentCategory) => {
      if (currentCategory === categoryValue) {
        return currentCategory;
      }

      return categoryValue;
    });
  };

  const handleMobileCategoryKeyDown = (event, currentIndex) => {
    let nextIndex = null;

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % categoryOptions.length;
    }

    if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + categoryOptions.length) % categoryOptions.length;
    }

    if (event.key === 'Home') {
      nextIndex = 0;
    }

    if (event.key === 'End') {
      nextIndex = categoryOptions.length - 1;
    }

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();

    mobileCategoryButtonsRef.current[nextIndex]?.focus();
  };

  const sectionBackgroundColor = menuSettings?.section_background_color || '#f5f2ee';

  return (
    <MenuWrapper id="menu" $sectionBackgroundColor={sectionBackgroundColor}>
      <MenuContainer>
        {showCategoryFilter && (
          <>
            <DesktopCategories role="group" aria-label="Filtra prodotti per categoria">
              {categoryOptions.map((category) => {
                const isActive = activeCategory === category.value;

                return (
                  <DesktopCategoryButton
                    key={category.key}
                    type="button"
                    $active={isActive}
                    $activeBackgroundColor={category.activeBackgroundColor}
                    $activeTextColor={category.activeTextColor}
                    $fontFamily={category.fontFamily}
                    $fontSize={category.fontSize}
                    aria-pressed={isActive}
                    onClick={() => {
                      handleCategorySelect(category.value);
                    }}
                  >
                    {category.label}
                  </DesktopCategoryButton>
                );
              })}
            </DesktopCategories>

            <MobileRailShell>
              <MobileCategoriesRail
                ref={mobileRailRef}
                role="group"
                aria-label="Filtra prodotti per categoria"
                onScroll={updateSwipeHint}
              >
                {categoryOptions.map((category, index) => {
                  const isActive = activeCategory === category.value;

                  return (
                    <MobileCategoryButton
                      key={category.key}
                      ref={(buttonElement) => {
                        mobileCategoryButtonsRef.current[index] = buttonElement;
                      }}
                      type="button"
                      title={category.label}
                      $active={isActive}
                      $activeBackgroundColor={category.activeBackgroundColor}
                      $activeTextColor={category.activeTextColor}
                      $fontFamily={category.fontFamily}
                      $fontSize={category.fontSize}
                      aria-pressed={isActive}
                      onClick={() => {
                        handleCategorySelect(category.value);
                      }}
                      onKeyDown={(event) => {
                        handleMobileCategoryKeyDown(event, index);
                      }}
                    >
                      {category.label}
                    </MobileCategoryButton>
                  );
                })}
              </MobileCategoriesRail>

              {swipeHintSide && (
                <SwipeHint key={swipeHintSide} $side={swipeHintSide} aria-hidden="true">
                  <SwipeHintChevron $side={swipeHintSide}>
                    {swipeHintSide === 'left' ? '‹' : '›'}
                  </SwipeHintChevron>
                </SwipeHint>
              )}
            </MobileRailShell>
          </>
        )}

        {products.length > 0 ? (
          <ProductGrid>
            {products.map((product) => (
              <MenuCard key={product.id} product={product} menuSettings={menuSettings} />
            ))}
          </ProductGrid>
        ) : (
          <EmptyState role="status">Nessun prodotto disponibile in questa categoria.</EmptyState>
        )}
      </MenuContainer>
    </MenuWrapper>
  );
}

export default MenuSection;
